// ============================================================
// UIRegistry — allows plugins to contribute UI components to named slots
// ============================================================

type Listener = () => void;

export class UIRegistry {
  private slots = new Map<string, unknown[]>();
  private snapshots = new Map<string, unknown[]>();
  private listeners: Listener[] = [];
  private static readonly EMPTY: readonly unknown[] = Object.freeze([]);

  /** Register a component for a named UI slot. */
  register(slot: string, component: unknown): void {
    const list = this.slots.get(slot);
    if (list) {
      if (!list.includes(component)) {
        list.push(component);
      }
    } else {
      this.slots.set(slot, [component]);
    }
    this.snapshots.set(slot, [...(this.slots.get(slot)!)]);
    this.notify();
  }

  /** Unregister a component from a named UI slot. */
  unregister(slot: string, component: unknown): void {
    const list = this.slots.get(slot);
    if (!list) return;
    const idx = list.indexOf(component);
    if (idx !== -1) {
      list.splice(idx, 1);
      if (list.length === 0) {
        this.slots.delete(slot);
        this.snapshots.delete(slot);
      } else {
        this.snapshots.set(slot, [...list]);
      }
      this.notify();
    }
  }

  /** Get all components registered for a slot (returns a stable reference). */
  getComponents(slot: string): readonly unknown[] {
    return this.snapshots.get(slot) ?? UIRegistry.EMPTY;
  }

  /** Subscribe to changes. Returns unsubscribe function. */
  subscribe(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx !== -1) this.listeners.splice(idx, 1);
    };
  }

  destroy(): void {
    this.slots.clear();
    this.snapshots.clear();
    this.listeners.length = 0;
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
